import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Download, Share, X, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const DISMISS_KEY = "qsg-install-banner-dismissed";
const DISMISS_DAYS = 7;

// Routes where the banner is allowed to appear
const ALLOWED_PREFIXES = ["/auth", "/dashboard", "/client/login", "/client/dashboard"];

export const InstallAppBanner = () => {
  const location = useLocation();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);
  const [showIosInstructions, setShowIosInstructions] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  const onAllowedRoute = ALLOWED_PREFIXES.some((p) => location.pathname.startsWith(p));

  useEffect(() => {
    if (!onAllowedRoute) {
      setVisible(false);
      return;
    }

    // Already installed (standalone display mode)
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      // @ts-ignore iOS Safari
      window.navigator.standalone === true;
    if (isStandalone) return;

    // Recently dismissed
    try {
      const dismissedAt = localStorage.getItem(DISMISS_KEY);
      if (dismissedAt) {
        const days = (Date.now() - parseInt(dismissedAt, 10)) / (1000 * 60 * 60 * 24);
        if (days < DISMISS_DAYS) return;
      }
    } catch {}

    const ua = navigator.userAgent;
    const iOS = /iPad|iPhone|iPod/.test(ua) && !/Android/.test(ua);
    setIsIOS(iOS);

    if (iOS) {
      // iOS has no install API — show instructions banner
      setVisible(true);
      return;
    }

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    const installedHandler = () => {
      setVisible(false);
      setDeferredPrompt(null);
    };
    window.addEventListener("appinstalled", installedHandler);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installedHandler);
    };
  }, [onAllowedRoute]);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, Date.now().toString());
    } catch {}
    setVisible(false);
    setShowIosInstructions(false);
  };

  const handleInstall = async () => {
    if (isIOS) {
      setShowIosInstructions((v) => !v);
      return;
    }
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      setVisible(false);
    }
    setDeferredPrompt(null);
  };

  if (!visible || !onAllowedRoute) return null;

  return (
    <div className="fixed top-0 inset-x-0 z-[60] animate-in slide-in-from-top duration-300">
      <div className="bg-primary text-primary-foreground shadow-lg">
        <div className="max-w-5xl mx-auto px-3 py-2.5 flex items-center gap-3">
          <div className="shrink-0 w-9 h-9 rounded-lg bg-primary-foreground/15 flex items-center justify-center">
            <Smartphone className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold leading-tight">Install QuoteSendGo</p>
            <p className="text-xs opacity-90 leading-tight truncate">
              Add to your home screen for instant access.
            </p>
          </div>
          <Button
            size="sm"
            variant="secondary"
            onClick={handleInstall}
            className="shrink-0 h-8 gap-1.5"
          >
            {isIOS ? <Share className="w-3.5 h-3.5" /> : <Download className="w-3.5 h-3.5" />}
            Install
          </Button>
          <button
            onClick={dismiss}
            aria-label="Dismiss"
            className="shrink-0 p-1 rounded-md hover:bg-primary-foreground/15 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        {isIOS && showIosInstructions && (
          <div className="border-t border-primary-foreground/20 bg-primary-foreground/5">
            <div className="max-w-5xl mx-auto px-3 py-2.5 text-xs space-y-1">
              <p className="font-medium">To install on iPhone / iPad:</p>
              <ol className="list-decimal list-inside space-y-0.5 opacity-95">
                <li>Tap the <strong>Share</strong> button in Safari</li>
                <li>Scroll and tap <strong>"Add to Home Screen"</strong></li>
                <li>Tap <strong>"Add"</strong> to confirm</li>
              </ol>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InstallAppBanner;
